const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/ß/g, 'ss');
const category = value => {
  const type = normalize(value);
  if (/hose|shorts|trouser|pants/.test(type)) return 'trousers';
  if (/jacke|mantel|coat|jacket/.test(type)) return 'jackets';
  if (/t-shirt|tshirt|longsleeve/.test(type)) return 'tshirts';
  if (/hemd|shirt/.test(type)) return 'shirts';
  if (/pullover|sweat|hood/.test(type)) return 'sweats';
  return type;
};

export function modelKey(product) {
  const title = normalize(product.title);
  const named = title.match(/["„“”]([^"„“”]+)["„“”]/);
  if (named) return normalize(product.vendor) + ':' + named[1].trim();
  return title.replace(/\b(schwarz|black|navy|dunkelblau|blau|blue|weiss|white|khaki|sand|beige|tabak|braun|brown|hellgrau|dunkelgrau|grau|grey|gray|grun|green|olive|oliv|turquoise|turkis|midnight|teal|rot|red|bordeaux|burgundy|cream|creme|ecru)\b/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
}

export function selectAlternatives(current, candidates, limit = 3) {
  const seen = new Set([modelKey(current)]);
  const groups = new Set([current.colorGroup].filter(Boolean));
  return candidates.filter(item => {
    if (String(item.id) === String(current.id) || !item.available || !item.hasImage || category(item.type) !== category(current.type)) return false;
    const key = modelKey(item);
    if (seen.has(key) || item.colorGroup && groups.has(item.colorGroup)) return false;
    seen.add(key);
    if (item.colorGroup) groups.add(item.colorGroup);
    return true;
  }).slice(0, limit);
}

export function canReplaceLooks(bundles, resolvedIds) {
  const offers = bundles.filter(bundle => bundle.type !== 'volume');
  return offers.length > 0 && offers.length <= 3 && offers.every(bundle =>
    bundle.resolution === 'bundle_product' && bundle.storefront?.show_on_pdp && resolvedIds.includes(bundle.id));
}

export function lookImages(parent, components) {
  const identity = value => { const url = new URL(value, 'https://eisdieler.org'); return url.hostname + url.pathname; };
  const complete = components.length > 1 && components.every(item => item.featured_image);
  if (complete && components.some(item => identity(item.featured_image) === identity(parent.featured_image))) {
    return components.map(item => ({ src:item.featured_image, alt:item.title }));
  }
  return [{ src:parent.featured_image, alt:parent.title }];
}

export function selectNewProducts(candidates, excludedIds = [], limit = 2, cutoff = 0) {
  const excluded = new Set(excludedIds.map(String));
  return candidates.filter(item => item.available && item.hasImage && !excluded.has(String(item.id)) && (Date.parse(item.publishedAt) || 0) >= cutoff)
    .sort((a, b) => (Date.parse(b.publishedAt) || 0) - (Date.parse(a.publishedAt) || 0)).slice(0, limit);
}

export function lookQuote(parent, components) {
  const price = Number(parent.price);
  const valid = !parent.price_varies && components.length > 0 && components.every(item =>
    item.price != null && Number.isFinite(Number(item.price)) && !item.price_varies);
  const original = valid ? components.reduce((total, item) => total + Number(item.price) * (Number(item.quantity) || 1), 0) : 0;
  return { price, original: original > price ? original : null, saving: original > price ? original - price : 0 };
}

if (typeof document !== 'undefined') {
  const initialized = new WeakSet();
  const observed = new WeakMap();
  const requests = new Map();
  const selector = '[data-eisdieler-discovery]';
  const read = node => JSON.parse(node.textContent);
  const rootPath = () => (window.Shopify?.routes?.root || '/').replace(/\/$/, '') + '/';
  function request(url, json = true) {
    if (!requests.has(url)) requests.set(url, fetch(url, { signal: AbortSignal.timeout(10000) }).then(response => {
      if (!response.ok) throw new Error('Discovery unavailable');
      return json ? response.json() : response.text();
    }));
    return requests.get(url);
  }
  function productURL(handle) { return rootPath() + 'products/' + encodeURIComponent(handle); }
  function text(tag, value, className) {
    const node = document.createElement(tag);
    node.textContent = value;
    if (className) node.className = className;
    return node;
  }
  function link(href, label, className) {
    const node = text('a', label, className);
    node.href = href;
    return node;
  }
  function money(value, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'de', { style: 'currency', currency }).format(value / 100);
  }
  function previewURL(path) {
    const url = new URL(path, location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }
  async function candidates(mount) {
    const url = previewURL(mount.dataset.collectionUrl);
    url.searchParams.set('section_id', 'eisdieler-discovery-products');
    url.searchParams.set('sort_by', 'created-descending');
    const html = await request(url.href, false);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return [...doc.querySelectorAll('[data-discovery-candidate]')].map(node => ({
      ...read(node.querySelector('[data-discovery-product]')), card: node.querySelector('template')
    })).filter(item => item.card);
  }
  async function fillProducts(mount) {
    const pool = await candidates(mount);
    const context = read(mount.querySelector('[data-discovery-context]'));
    const selected = mount.dataset.eisdielerDiscovery === 'alternatives'
      ? selectAlternatives(context, pool, Number(mount.dataset.limit) || 3)
      : selectNewProducts(pool, context.excludedIds, 2, context.cutoff);
    if (!selected.length || !mount.isConnected) return;
    const grid = mount.querySelector('[data-discovery-grid]');
    grid.replaceChildren(...selected.map(item => item.card.content.cloneNode(true)));
    mount.hidden = false;
    mount.dataset.loaded = 'true';
  }
  async function lookData(bundle) {
    if (!bundle.bundle_product_handle || !/^[a-z0-9-]+$/.test(bundle.bundle_product_handle)) return null;
    const parent = await request(productURL(bundle.bundle_product_handle) + '.js');
    if (!parent.available || !parent.featured_image) return null;
    const components = await Promise.all((bundle.components || []).map(async component => {
      if (!component.handle || !/^[a-z0-9-]+$/.test(component.handle)) throw new Error('Invalid set product');
      return { ...await request(productURL(component.handle) + '.js'), quantity: component.quantity || 1 };
    }));
    return { bundle, parent, components };
  }
  function renderLook(item, copy, currency) {
    const { parent, components } = item;
    const layout = document.createElement('div');
    layout.className = 'eisdieler-look__layout';
    const imageLink = link(productURL(parent.handle), '', 'eisdieler-look__image');
    imageLink.setAttribute('aria-label', parent.title);
    const photos = lookImages(parent, components);
    if (photos.length > 1) imageLink.classList.add('eisdieler-look__image--components');
    photos.forEach(photo => {
      const imageURL = new URL(photo.src, location.origin);
      if (imageURL.protocol !== 'https:' || !['cdn.shopify.com', location.hostname].includes(imageURL.hostname)) throw new Error('Invalid set image');
      imageURL.searchParams.set('width', photos.length > 1 ? '500' : '900');
      const image = document.createElement('img');
      Object.assign(image, { src: imageURL.href, alt: photo.alt, width: 600, height: 800, loading: 'lazy', decoding: 'async' });
      imageLink.append(image);
    });
    const content = document.createElement('div');
    content.className = 'eisdieler-look__content';
    content.append(text('p', copy.complete_look, 'eisdieler-discovery__eyebrow'), text('h3', parent.title));
    const list = document.createElement('ul');
    list.className = 'eisdieler-look__components';
    components.forEach(component => {
      const row = document.createElement('li');
      row.append(link(productURL(component.handle), component.title + (component.quantity > 1 ? ' × ' + component.quantity : '')),
        text('span', (component.price_varies ? copy.from + ' ' : '') + money(component.price, currency)));
      list.append(row);
    });
    content.append(list);
    const quote = lookQuote(parent, components);
    const priceRow = document.createElement('div');
    priceRow.className = 'eisdieler-look__price';
    priceRow.append(text('span', copy.set_price));
    const values = document.createElement('span');
    if (quote.original) values.append(text('s', money(quote.original, currency)));
    values.append(text('strong', (parent.price_varies ? copy.from + ' ' : '') + money(quote.price, currency)));
    priceRow.append(values);
    content.append(priceRow);
    if (quote.saving) content.append(text('p', copy.saving.replace('__AMOUNT__', money(quote.saving, currency)), 'eisdieler-look__saving'));
    content.append(link(productURL(parent.handle), copy.view_look, 'eisdieler-look__button'));
    layout.append(imageLink, content);
    return layout;
  }
  async function fillLook(mount) {
    const payload = await request(rootPath() + 'apps/feedback/bundles/for-product/' + encodeURIComponent(mount.dataset.productId));
    if (!payload.ok) return;
    const bundles = (payload.bundles || []).filter(bundle => bundle.type !== 'volume' && bundle.resolution === 'bundle_product' && bundle.storefront?.show_on_pdp).slice(0, 3);
    const results = await Promise.allSettled(bundles.map(lookData));
    const items = results.filter(result => result.status === 'fulfilled' && result.value).map(result => result.value);
    if (!canReplaceLooks(payload.bundles || [], items.map(item => item.bundle.id)) || !mount.isConnected) return;
    const copy = read(mount.querySelector('[data-discovery-context]'));
    const host = mount.querySelector('[data-discovery-look-content]');
    const currency = mount.dataset.currency;
    const panels = items.map((item, index) => {
      const panel = renderLook(item, copy, currency);
      panel.id = mount.id + '-look-' + index;
      panel.hidden = index > 0;
      return panel;
    });
    if (panels.length > 1) {
      const tabs = document.createElement('div');
      tabs.className = 'eisdieler-look__choices';
      tabs.setAttribute('role', 'tablist');
      tabs.setAttribute('aria-label', copy.choose_look);
      items.forEach((item, index) => {
        const button = text('button', item.parent.title);
        button.type = 'button';
        button.id = mount.id + '-choice-' + index;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panels[index].id);
        panels[index].setAttribute('role', 'tabpanel');
        panels[index].setAttribute('aria-labelledby', button.id);
        const select = selected => {
          [...tabs.children].forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === selected)); tab.tabIndex = i === selected ? 0 : -1; panels[i].hidden = i !== selected; });
        };
        button.setAttribute('aria-selected', String(index === 0));
        button.tabIndex = index === 0 ? 0 : -1;
        button.addEventListener('click', () => select(index));
        button.addEventListener('keydown', event => {
          const next = event.key === 'ArrowRight' ? (index + 1) % items.length : event.key === 'ArrowLeft' ? (index + items.length - 1) % items.length : event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : -1;
          if (next >= 0) { event.preventDefault(); select(next); tabs.children[next].focus(); }
        });
        tabs.append(button);
      });
      host.append(tabs);
    }
    host.append(...panels);
    mount.hidden = false;
    mount.dataset.loaded = 'true';
    document.querySelectorAll('[data-knotly-bdl][data-surface="pdp"]').forEach(old => {
      if (old.dataset.productId === mount.dataset.productId) old.dataset.eisdielerLookReplaced = 'true';
    });
  }
  const observer = new IntersectionObserver(entries => entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    observer.unobserve(entry.target);
    load(observed.get(entry.target));
  }), { rootMargin: '800px' });
  function load(mount) {
    (mount.dataset.eisdielerDiscovery === 'look' ? fillLook(mount) : fillProducts(mount)).catch(() => {});
  }
  function init(scope) {
    const mounts = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) mounts.unshift(scope);
    mounts.forEach(mount => {
      if (initialized.has(mount)) return;
      initialized.add(mount);
      if (mount.dataset.eisdielerDiscovery === 'look') load(mount);
      else {
        const anchor = mount.parentElement;
        observed.set(anchor, mount);
        observer.observe(anchor);
      }
    });
  }
  function start() {
    init(document);
    new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === 1) init(node);
    }))).observe(document.getElementById('MainContent') || document.body, { childList:true, subtree:true });
    document.addEventListener('shopify:section:load', event => init(event.target));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once:true });
  else start();
}
